/**
 * Cloudinary Image Transformation Service
 *
 * This service provides methods for transforming images using Cloudinary's URL-based API.
 * It supports cropping, resizing, and various other image transformations.
 *
 * SETUP:
 * Add these environment variables to your .env.development file:
 * - VITE_CLOUDINARY_NAME=your_cloud_name
 * - VITE_CLOUDINARY_API_KEY=your_api_key (optional for basic transformations)
 * - VITE_CLOUDINARY_API_SECRET=your_api_secret (optional for basic transformations)
 *
 * USAGE EXAMPLES:
 *
 * import { cloudinaryService } from '@/helpers';
 *
 * // Basic image cropping
 * const croppedUrl = cloudinaryService.cropImage(
 *   'https://example.com/image.jpg',
 *   300, 200
 * );
 *
 * // Advanced transformation with options
 * const transformedUrl = cloudinaryService.transformImage(imageUrl, {
 *   width: 400,
 *   height: 300,
 *   crop: 'fill',
 *   quality: 'auto',
 *   format: 'webp',
 *   gravity: 'face'
 * });
 *
 * // Simple resize (maintains aspect ratio)
 * const resizedUrl = cloudinaryService.resizeImage(imageUrl, 800);
 *
 * // Optimize for web (compression + format)
 * const optimizedUrl = cloudinaryService.optimizeImage(imageUrl, 1200);
 *
 * // Smart transformation with automatic fallback validation
 * const smartResult = await cloudinaryService.smartTransformImage(imageUrl, {
 *   width: 300,
 *   height: 300,
 *   crop: 'crop',
 *   quality: 'auto'
 * });
 * console.log('Using:', smartResult.url, 'Cloudinary:', smartResult.isCloudinary);
 *
 * SUPPORTED INPUT FORMATS:
 * - External URLs: https://example.com/image.jpg (with proper URL encoding)
 * - Cloudinary URLs: https://res.cloudinary.com/cloud/image/upload/...
 * - Public IDs: "sample-image" or "folder/sample-image"
 *
 * AUTOMATIC FEATURES:
 * - URL encoding for special characters in external URLs
 * - Fetch URL validation with automatic fallback to original
 * - Smart domain detection (restomenu domain handling)
 * - Error handling with graceful degradation
 */

export interface CloudinaryTransformOptions {
  width?: number;
  height?: number;
  crop?:
    | "scale"
    | "fit"
    | "fill"
    | "crop"
    | "thumb"
    | "limit"
    | "mfit"
    | "mpad";
  quality?: "auto" | number;
  format?: "auto" | "jpg" | "png" | "webp" | "avif";
  gravity?:
    | "auto"
    | "face"
    | "center"
    | "north"
    | "south"
    | "east"
    | "west"
    | "north_east"
    | "north_west"
    | "south_east"
    | "south_west";
  fetchFormat?: "auto";
  flags?: string[];
}

export interface CloudinaryConfig {
  cloudName: string;
  apiKey?: string;
  apiSecret?: string;
}

class CloudinaryService {
  private config: CloudinaryConfig;

  constructor() {
    this.config = {
      cloudName: import.meta.env.VITE_CLOUDINARY_NAME || "",
      apiKey: import.meta.env.VITE_CLOUDINARY_API_KEY || "",
      apiSecret: import.meta.env.VITE_CLOUDINARY_API_SECRET || "",
    };

    if (!this.config.cloudName) {
      console.warn(
        "Cloudinary cloud name is not configured. Please set VITE_CLOUDINARY_NAME in your environment variables."
      );
    }
  }

  /**
   * Transforms an image URL using Cloudinary transformations
   *
   * @param imageUrl - Original image URL or Cloudinary public ID
   * @param options - Transformation options (width, height, crop, etc.)
   * @returns Transformed Cloudinary URL
   */
  public transformImage(
    imageUrl: string,
    options: CloudinaryTransformOptions = {}
  ): string {
    if (!this.config.cloudName) {
      console.error("Cloudinary cloud name is not configured");
      return imageUrl; // Return original URL if not configured
    }

    try {
      // Check if it's already a Cloudinary URL
      if (imageUrl.includes("cloudinary.com")) {
        return this.modifyCloudinaryUrl(imageUrl, options);
      }

      // Build transformation string
      const transformations = this.buildTransformationString(options);

      // If it's an external URL, try fetch transformation with fallback
      if (this.isExternalUrl(imageUrl)) {
        const encodedUrl = encodeURIComponent(imageUrl);
        return `https://res.cloudinary.com/${this.config.cloudName}/image/fetch/${transformations}/${encodedUrl}`;
      }

      // Assume it's a public ID
      return `https://res.cloudinary.com/${this.config.cloudName}/image/upload/${transformations}/${imageUrl}`;
    } catch (error) {
      console.error("Error transforming image with Cloudinary:", error);
      return imageUrl; // Return original URL on error
    }
  }

  /**
   * Creates a cropped version of an image
   *
   * @param imageUrl - Original image URL
   * @param width - Target width
   * @param height - Target height
   * @param gravity - Crop gravity (default: 'auto')
   * @returns Cropped image URL
   */
  public cropImage(
    imageUrl: string,
    width: number,
    height: number,
    gravity: CloudinaryTransformOptions["gravity"] = "auto"
  ): string {
    return this.transformImage(imageUrl, {
      width,
      height,
      crop: "crop",
      gravity,
      quality: "auto",
      format: "auto",
    });
  }

  /**
   * Creates a resized version of an image (maintains aspect ratio)
   *
   * @param imageUrl - Original image URL
   * @param width - Target width
   * @param height - Target height (optional)
   * @returns Resized image URL
   */
  public resizeImage(imageUrl: string, width: number, height?: number): string {
    const options: CloudinaryTransformOptions = {
      width,
      crop: "scale",
      quality: "auto",
      format: "auto",
    };

    if (height) {
      options.height = height;
      options.crop = "fit";
    }

    return this.transformImage(imageUrl, options);
  }

  /**
   * Creates an optimized version of an image
   *
   * @param imageUrl - Original image URL
   * @param maxWidth - Maximum width (default: 1200)
   * @returns Optimized image URL
   */
  public optimizeImage(imageUrl: string, maxWidth: number = 1200): string {
    return this.transformImage(imageUrl, {
      width: maxWidth,
      crop: "limit",
      quality: "auto",
      format: "auto",
      fetchFormat: "auto",
    });
  }

  /**
   * Builds transformation string from options
   */
  private buildTransformationString(
    options: CloudinaryTransformOptions
  ): string {
    const transformations: string[] = [];

    if (options.width) transformations.push(`w_${options.width}`);
    if (options.height) transformations.push(`h_${options.height}`);
    if (options.crop) transformations.push(`c_${options.crop}`);
    if (options.quality) transformations.push(`q_${options.quality}`);
    if (options.format) transformations.push(`f_${options.format}`);
    if (options.gravity) transformations.push(`g_${options.gravity}`);
    if (options.fetchFormat) transformations.push(`f_${options.fetchFormat}`);
    if (options.flags && options.flags.length > 0) {
      transformations.push(`fl_${options.flags.join(",")}`);
    }

    return transformations.join(",");
  }

  /**
   * Modifies existing Cloudinary URL with new transformations
   */
  private modifyCloudinaryUrl(
    url: string,
    options: CloudinaryTransformOptions
  ): string {
    const transformations = this.buildTransformationString(options);

    if (!transformations) return url;

    // Enhanced regex to properly handle Cloudinary URL structure
    // Pattern: /image/(upload|fetch)/[optional_transformations]/[optional_version]/public_id
    const cloudinaryMatch = url.match(/\/image\/(upload|fetch)\/(.+)/);

    if (cloudinaryMatch) {
      const [, type, pathPart] = cloudinaryMatch;
      const pathSegments = pathPart.split("/");

      // Find where transformations should be inserted
      let versionIndex = -1;

      // Look for version number (starts with 'v' followed by digits)
      for (let i = 0; i < pathSegments.length; i++) {
        if (pathSegments[i].match(/^v\d+$/)) {
          versionIndex = i;
          break;
        }
      }

      // If we found a version, transformations go before it
      if (versionIndex > 0) {
        // Check if there are existing transformations before the version
        const beforeVersion = pathSegments.slice(0, versionIndex).join("/");
        if (beforeVersion && !beforeVersion.match(/^v\d+$/)) {
          // There are existing transformations
          const existingTransforms = beforeVersion;
          const newTransforms = `${existingTransforms},${transformations}`;
          pathSegments.splice(0, versionIndex, newTransforms);
        } else {
          // No existing transformations, insert new ones
          pathSegments.splice(versionIndex, 0, transformations);
        }
      } else {
        // No version found, check if first segment looks like transformations
        const firstSegment = pathSegments[0];
        if (
          firstSegment &&
          firstSegment.includes("_") &&
          !firstSegment.includes(".")
        ) {
          // First segment looks like transformations
          const existingTransforms = firstSegment;
          const newTransforms = `${existingTransforms},${transformations}`;
          pathSegments[0] = newTransforms;
        } else {
          // No existing transformations, insert at the beginning
          pathSegments.unshift(transformations);
        }
      }

      const newPath = pathSegments.join("/");
      return url.replace(
        /\/image\/(upload|fetch)\/(.+)/,
        `/image/${type}/${newPath}`
      );
    }

    return url;
  }

  /**
   * Checks if URL is external (not a Cloudinary public ID)
   */
  private isExternalUrl(url: string): boolean {
    return (
      url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("//")
    );
  }

  /**
   * Gets the current configuration
   */
  public getConfig(): CloudinaryConfig {
    return { ...this.config };
  }

  /**
   * Updates configuration
   */
  public updateConfig(newConfig: Partial<CloudinaryConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Validates if a Cloudinary fetch URL is accessible
   *
   * @param cloudinaryUrl - The Cloudinary fetch URL to validate
   * @returns Promise that resolves to true if URL is accessible
   */
  public async validateFetchUrl(cloudinaryUrl: string): Promise<boolean> {
    if (typeof window === "undefined") {
      // Server-side: can't validate, assume valid
      return true;
    }

    try {
      // Use HEAD request to check if URL is accessible without downloading content
      await fetch(cloudinaryUrl, {
        method: "HEAD",
        mode: "no-cors", // Allow cross-origin requests
      });

      // With no-cors mode, we can't check response.ok,
      // but if no error is thrown, the URL is likely accessible
      return true;
    } catch (error) {
      console.warn("Cloudinary fetch URL validation failed:", error);
      return false;
    }
  }

  /**
   * Smart image transformation with automatic fallback
   *
   * @param imageUrl - Original image URL
   * @param options - Transformation options
   * @returns Promise with optimized URL and fallback info
   */
  public async smartTransformImage(
    imageUrl: string,
    options: CloudinaryTransformOptions = {}
  ): Promise<{ url: string; isCloudinary: boolean; fallbackUsed: boolean }> {
    // First try Cloudinary transformation
    const cloudinaryUrl = this.transformImage(imageUrl, options);

    // If it's the same as original (no transformation applied), return original
    if (cloudinaryUrl === imageUrl) {
      return {
        url: imageUrl,
        isCloudinary: false,
        fallbackUsed: false,
      };
    }

    // For external URLs, validate the Cloudinary fetch URL
    if (this.isExternalUrl(imageUrl)) {
      const isValid = await this.validateFetchUrl(cloudinaryUrl);

      if (!isValid) {
        console.warn("Cloudinary fetch failed, using original URL:", imageUrl);
        return {
          url: imageUrl,
          isCloudinary: false,
          fallbackUsed: true,
        };
      }
    }

    return {
      url: cloudinaryUrl,
      isCloudinary: true,
      fallbackUsed: false,
    };
  }

  /**
   * Optimizes Restomenu static images using CSS object-fit for transformations
   * This is a fallback for when Cloudinary fetch doesn't work with the domain
   *
   * @param imageUrl - Original restomenu image URL
   * @param width - Target width
   * @param height - Target height (optional)
   * @returns Object with URL and CSS styles for optimization
   */
  public optimizeRestomenuImage(
    imageUrl: string,
    width: number,
    height?: number
  ) {
    // Return original URL with CSS properties for client-side optimization
    return {
      url: imageUrl,
      styles: {
        width: `${width}px`,
        height: height ? `${height}px` : `${width}px`, // Square by default
        objectFit: "cover" as const,
        objectPosition: "center" as const,
        borderRadius: "16px",
        backgroundColor: "#EEE",
        // Add loading optimization
        loading: "lazy" as const,
        decoding: "async" as const,
      },
    };
  }
}

// Create and export singleton instance
export const cloudinaryService = new CloudinaryService();

// Export the class for custom instances
export { CloudinaryService };
